(function () {
    'use strict';

    if (window.MemoraRuntime) return;

    const DEFAULT_TIMEOUT_MS = 20000;
    const maxStudySourceBytes = 500 * 1024 * 1024;

    function validateStudySourceSize(size, filename = 'Lecture') {
        if (Number(size) > maxStudySourceBytes) {
            throw new Error(`${filename || 'Lecture'} exceeds the 500 MB PDF/PowerPoint input limit.`);
        }
    }
    const resourcePromises = new Map();
    const failedResourceKeys = new Set();
    let pdfRuntimePromise = null;
    let pptxRuntimePromise = null;
    let katexRuntimePromise = null;
    let resourceRetrySequence = 0;
    const resourceUrls = Object.freeze({
        confettiScript: 'js/vendor/canvas-confetti/1.6.0/dist/confetti.browser.js',
        googleIdentityScript: 'https://accounts.google.com/gsi/client',
        katexStyle: 'js/vendor/katex/0.16.9/dist/katex.min.css',
        katexScript: 'js/vendor/katex/0.16.9/dist/katex.min.js',
        katexAutoRenderScript: 'js/vendor/katex/0.16.9/dist/contrib/auto-render.min.js',
        pdfStyle: 'js/vendor/pdfjs/4.10.38/web/pdf_viewer.css',
        pdfScript: 'js/vendor/pdfjs/4.10.38/pdf-core.mjs',
        pdfViewerScript: 'js/vendor/pdfjs/4.10.38/web/pdf_viewer.mjs',
        pdfWorkerScript: 'js/vendor/pdfjs/4.10.38/build/pdf.worker.min.mjs',
        pdfCMapUrl: 'js/vendor/pdfjs/4.10.38/cmaps/',
        pdfStandardFontDataUrl: 'js/vendor/pdfjs/4.10.38/standard_fonts/',
        pptxScript: 'js/vendor/pptx-renderer/1.2.4/aiden0z-pptx-renderer.browser.iife.js',
        tiffScript: 'js/vendor/utif/3.1.0/UTIF.js'
    });

    function getAbsoluteResourceUrl(url) {
        return new URL(String(url || ''), document.baseURI).href;
    }

    function findResourceElement(selector, url) {
        const absoluteUrl = getAbsoluteResourceUrl(url);
        return Array.from(document.querySelectorAll(selector)).find((element) => {
            const elementUrl = element.src || element.href || '';
            return getAbsoluteResourceUrl(elementUrl) === absoluteUrl;
        }) || null;
    }

    function getLoadedValue(test) {
        if (typeof test !== 'function') return true;
        try {
            return test() || null;
        } catch (_error) {
            return null;
        }
    }

    function getRetryResourceUrl(src) {
        const retryUrl = new URL(String(src || ''), document.baseURI);
        retryUrl.searchParams.set('memoraRetry', String(++resourceRetrySequence));
        return retryUrl.href;
    }

    function ensureScriptLoaded({ src, key = src, test = null, timeoutMs = DEFAULT_TIMEOUT_MS, attributes = {} } = {}) {
        const loadedValue = getLoadedValue(test);
        if (loadedValue) return Promise.resolve(loadedValue);
        if (!src) return Promise.reject(new Error('A script URL is required.'));

        const promiseKey = `script:${key || getAbsoluteResourceUrl(src)}`;
        if (resourcePromises.has(promiseKey)) return resourcePromises.get(promiseKey);

        const loadPromise = new Promise((resolve, reject) => {
            let settled = false;
            let timeoutId = 0;
            let script = findResourceElement('script[src]', src);
            const retryingFailedScript = failedResourceKeys.has(promiseKey)
                || script?.dataset?.memoraLoadFailed === 'true';

            if (retryingFailedScript) {
                script?.remove();
                script = null;
            }

            const cleanup = () => {
                if (timeoutId) window.clearTimeout(timeoutId);
                if (!script) return;
                script.removeEventListener('load', handleLoaded);
                script.removeEventListener('error', handleFailed);
            };
            const rejectLoad = (message, cause = null) => {
                if (settled) return;
                settled = true;
                cleanup();
                resourcePromises.delete(promiseKey);
                failedResourceKeys.add(promiseKey);
                if (script?.dataset?.memoraRuntimeOwned === 'true') script.remove();
                const error = new Error(message);
                if (cause) error.cause = cause;
                reject(error);
            };
            const handleLoaded = () => {
                if (settled) return;
                const value = getLoadedValue(test);
                if (!value) {
                    rejectLoad(`Loaded script did not expose its expected runtime: ${src}`);
                    return;
                }
                settled = true;
                cleanup();
                failedResourceKeys.delete(promiseKey);
                if (script?.dataset) script.dataset.memoraLoaded = 'true';
                resolve(value);
            };
            const handleFailed = (event) => {
                if (script?.dataset) script.dataset.memoraLoadFailed = 'true';
                rejectLoad(`Failed to load script: ${src}`, event?.error || null);
            };

            const shouldAppend = !script;
            if (shouldAppend) {
                script = document.createElement('script');
                script.src = retryingFailedScript ? getRetryResourceUrl(src) : src;
                script.async = true;
                script.dataset.memoraRuntimeOwned = 'true';
                Object.entries(attributes || {}).forEach(([name, value]) => {
                    if (value !== undefined && value !== null) script.setAttribute(name, String(value));
                });
            }

            script.addEventListener('load', handleLoaded, { once: true });
            script.addEventListener('error', handleFailed, { once: true });
            timeoutId = window.setTimeout(() => {
                rejectLoad(`Timed out loading script after ${timeoutMs}ms: ${src}`);
            }, Math.max(1000, Number(timeoutMs) || DEFAULT_TIMEOUT_MS));
            if (shouldAppend) document.head.appendChild(script);

            const valueAfterBinding = getLoadedValue(test);
            if (valueAfterBinding) handleLoaded();
        });

        resourcePromises.set(promiseKey, loadPromise);
        return loadPromise;
    }

    function ensureStyleLoaded({ href, key = href, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
        if (!href) return Promise.reject(new Error('A stylesheet URL is required.'));
        const promiseKey = `style:${key || getAbsoluteResourceUrl(href)}`;
        if (resourcePromises.has(promiseKey)) return resourcePromises.get(promiseKey);

        const loadPromise = new Promise((resolve, reject) => {
            let settled = false;
            let timeoutId = 0;
            let link = findResourceElement('link[rel="stylesheet"][href]', href);

            if (link?.dataset?.memoraLoadFailed === 'true') {
                link.remove();
                link = null;
            }

            const cleanup = () => {
                if (timeoutId) window.clearTimeout(timeoutId);
                if (!link) return;
                link.removeEventListener('load', handleLoaded);
                link.removeEventListener('error', handleFailed);
            };
            const rejectLoad = (message) => {
                if (settled) return;
                settled = true;
                cleanup();
                resourcePromises.delete(promiseKey);
                if (link?.dataset?.memoraRuntimeOwned === 'true') link.remove();
                reject(new Error(message));
            };
            const handleLoaded = () => {
                if (settled) return;
                settled = true;
                cleanup();
                if (link?.dataset) link.dataset.memoraLoaded = 'true';
                resolve(link);
            };
            const handleFailed = () => {
                if (link?.dataset) link.dataset.memoraLoadFailed = 'true';
                rejectLoad(`Failed to load stylesheet: ${href}`);
            };

            const shouldAppend = !link;
            if (shouldAppend) {
                link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = href;
                link.dataset.memoraRuntimeOwned = 'true';
            }

            link.addEventListener('load', handleLoaded, { once: true });
            link.addEventListener('error', handleFailed, { once: true });
            timeoutId = window.setTimeout(() => {
                rejectLoad(`Timed out loading stylesheet after ${timeoutMs}ms: ${href}`);
            }, Math.max(1000, Number(timeoutMs) || DEFAULT_TIMEOUT_MS));
            if (shouldAppend) document.head.appendChild(link);

            if (link.dataset.memoraLoaded === 'true' || link.sheet) handleLoaded();
        });

        resourcePromises.set(promiseKey, loadPromise);
        return loadPromise;
    }

    function ensurePdfRuntimeLoaded() {
        if (pdfRuntimePromise) return pdfRuntimePromise;
        if (window.pdfjsLib?.getDocument && window.pdfjsViewer?.PDFViewer) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = resourceUrls.pdfWorkerScript;
            pdfRuntimePromise = Promise.resolve({ pdfjsLib: window.pdfjsLib, pdfjsViewer: window.pdfjsViewer });
            return pdfRuntimePromise;
        }
        pdfRuntimePromise = (async () => {
            await Promise.all([
                ensureStyleLoaded({ href: resourceUrls.pdfStyle, key: 'pdf-viewer-style' }),
                ensureScriptLoaded({
                    src: resourceUrls.pdfScript,
                    key: 'pdf-core',
                    attributes: { type: 'module' },
                    test: () => window.pdfjsLib?.getDocument && window.pdfjsLib
                })
            ]);
            await ensureScriptLoaded({
                src: resourceUrls.pdfViewerScript,
                key: 'pdf-viewer',
                attributes: { type: 'module' },
                test: () => window.pdfjsViewer?.PDFViewer && window.pdfjsViewer
            });
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = resourceUrls.pdfWorkerScript;
            return { pdfjsLib: window.pdfjsLib, pdfjsViewer: window.pdfjsViewer };
        })().catch((error) => {
            pdfRuntimePromise = null;
            throw error;
        });
        return pdfRuntimePromise;
    }

    // Shape ids whose last main-sequence build effect is an exit. A paragraph-level exit (txEl)
    // leaves the rest of its shape on screen, so it does not hide the shape.
    function readPptxEndStateExitIds(slideXml) {
        const exited = new Set();
        if (typeof slideXml !== 'string' || !slideXml.includes('presetClass="exit"') || typeof DOMParser !== 'function') return exited;
        const doc = new DOMParser().parseFromString(slideXml, 'application/xml');
        if (doc.getElementsByTagName('parsererror').length) return exited;
        const lastClassById = new Map();
        Array.from(doc.getElementsByTagNameNS('*', 'cTn'))
            .filter((node) => node.getAttribute('nodeType') === 'mainSeq')
            .forEach((sequence) => {
                Array.from(sequence.getElementsByTagNameNS('*', 'cTn')).forEach((node) => {
                    const presetClass = node.getAttribute('presetClass');
                    if (presetClass !== 'entr' && presetClass !== 'exit') return;
                    const target = node.getElementsByTagNameNS('*', 'spTgt')[0];
                    const id = target?.getAttribute('spid');
                    if (!id || target.getElementsByTagNameNS('*', 'txEl').length) return;
                    lastClassById.set(id, presetClass);
                });
            });
        lastClassById.forEach((presetClass, id) => { if (presetClass === 'exit') exited.add(id); });
        return exited;
    }

    // Group children stay raw XML wrappers until they render, so their id is read off cNvPr.
    function getPptxNodeShapeId(node) {
        if (node?.id !== undefined) return String(node.id);
        return String(node?.el?.getElementsByTagNameNS?.('*', 'cNvPr')?.[0]?.getAttribute('id') ?? '');
    }

    function withoutPptxExitedNodes(nodes, exitedIds) {
        let changed = false;
        const kept = [];
        nodes.forEach((node) => {
            if (exitedIds.has(getPptxNodeShapeId(node))) {
                changed = true;
                return;
            }
            if (node?.nodeType === 'group' && Array.isArray(node.children)) {
                const children = withoutPptxExitedNodes(node.children, exitedIds);
                if (children !== node.children) {
                    changed = true;
                    kept.push({ ...node, children });
                    return;
                }
            }
            kept.push(node);
        });
        return changed ? kept : nodes;
    }

    // A frozen snapshot of an animated slide stacks every build on top of each other: a picture
    // that appeared and then exited covers the bullets that replaced it. Slides render in the
    // state the class last saw them, and memoraAllNodes keeps the full set for text extraction.
    function applyPptxAnimationEndState(runtime, presentation) {
        (presentation?.slides || []).forEach((slide) => {
            try {
                const exitedIds = readPptxEndStateExitIds(slide?.sourceXml);
                if (!exitedIds.size) return;
                runtime.materializeSlideNodes(presentation, slide);
                const nodes = withoutPptxExitedNodes(slide.nodes || [], exitedIds);
                if (nodes === slide.nodes) return;
                slide.memoraAllNodes = slide.nodes;
                slide.nodes = nodes;
            } catch (error) {
                console.warn('[PowerPoint] Animation end state could not be applied:', error);
            }
        });
        return presentation;
    }

    function ensurePptxRuntimeLoaded() {
        if (pptxRuntimePromise) return pptxRuntimePromise;
        // Slides often carry TIFF screenshots, which browsers cannot decode; the renderer converts
        // them through window.UTIF when it is present. A failed load only leaves TIFFs blank.
        const tiffDecoder = ensureScriptLoaded({
            src: withAssetVersion(resourceUrls.tiffScript),
            key: 'tiff-decoder',
            test: () => window.UTIF
        }).catch(() => null);
        pptxRuntimePromise = Promise.all([ensureScriptLoaded({
            src: withAssetVersion(resourceUrls.pptxScript),
            key: 'pptx-runtime',
            test: () => window.MemoraPptxRuntime
        }), tiffDecoder])
            .then(([runtime]) => {
                if (!runtime?.PptxViewer
                    || !runtime?.parseZipLazyMedia
                    || !runtime?.buildPresentation
                    || !runtime?.materializeAllSlideNodes
                    || !runtime?.materializeSlideNodes
                    || !runtime?.buildTextIndex) {
                    throw new Error('The local PowerPoint viewer did not expose its expected runtime.');
                }
                // Shared by source viewing/text extraction and static-slide PDF compression.
                // Large lecture videos remain lazy; parsing must succeed before compression runs.
                return {
                    ...runtime,
                    buildPresentation: (files, options) => applyPptxAnimationEndState(runtime, runtime.buildPresentation(files, options)),
                    RECOMMENDED_ZIP_LIMITS: Object.freeze({
                        ...runtime.RECOMMENDED_ZIP_LIMITS,
                        maxEntryUncompressedBytes: maxStudySourceBytes,
                        maxTotalUncompressedBytes: 2 * maxStudySourceBytes,
                        maxMediaBytes: maxStudySourceBytes
                    })
                };
            })
            .catch((error) => {
                pptxRuntimePromise = null;
                throw error;
            });
        return pptxRuntimePromise;
    }

    function ensureGoogleIdentityLoaded() {
        return ensureScriptLoaded({
            src: resourceUrls.googleIdentityScript,
            key: 'google-identity',
            test: () => window.google?.accounts?.id && window.google.accounts.id
        });
    }

    function ensureConfettiLoaded() {
        return ensureScriptLoaded({
            src: resourceUrls.confettiScript,
            key: 'confetti',
            test: () => typeof window.confetti === 'function' && window.confetti
        });
    }

    function ensureKatexLoaded() {
        if (katexRuntimePromise) return katexRuntimePromise;
        if (typeof window.renderMathInElement === 'function') {
            katexRuntimePromise = Promise.resolve(window.renderMathInElement);
            return katexRuntimePromise;
        }
        katexRuntimePromise = (async () => {
            await Promise.all([
                ensureStyleLoaded({ href: resourceUrls.katexStyle, key: 'katex-style' }),
                ensureScriptLoaded({
                    src: resourceUrls.katexScript,
                    key: 'katex-core',
                    test: () => window.katex?.render && window.katex
                })
            ]);
            await ensureScriptLoaded({
                src: resourceUrls.katexAutoRenderScript,
                key: 'katex-auto-render',
                test: () => typeof window.renderMathInElement === 'function' && window.renderMathInElement
            });
            return window.renderMathInElement;
        })().catch((error) => {
            katexRuntimePromise = null;
            throw error;
        });
        return katexRuntimePromise;
    }

    function containsMathMarkup(root) {
        const text = String(root?.textContent || '');
        return /\$\$[\s\S]+?\$\$|\$[^$\n]+\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]/.test(text);
    }

    async function renderMath(root, options = {}) {
        if (!root || !containsMathMarkup(root)) return false;
        const render = await ensureKatexLoaded();
        if (!root || typeof render !== 'function') return false;
        render(root, options);
        return true;
    }

    /* ---------------------------------------------------------------------
       FEATURE MODULES
       Secondary product surfaces ship as classic scripts exactly like the eager
       bundle, but load on intent instead of on every page load. Order inside a
       feature is preserved and declared dependencies load first, so the shared
       flat global scope always sees the same declaration order it saw when every
       module was listed in index.html.

       Cache-busting rides on the <meta name="memora-build"> stamp that
       scripts/bump-asset-versions.mjs already maintains for index.html.
       --------------------------------------------------------------------- */
    const featureModules = Object.freeze({
        ai: {
            label: 'Ask Memoraful and Learn',
            scripts: [
                // Answer icon geometry. Shared with the study guide, calendar and clinic,
                // which all reach it through deps: ['ai'], so this is the only place it is
                // listed. Keep it first: app.ai.js reads these tables from the flat global
                // scope and the loader replays this order.
                { src: 'js/modules/app.icons.js', test: () => typeof window.getMemoraAnswerIconSvg === 'function' },
                // Probe a symbol the feature bridge does not stub, otherwise the stub
                // itself reads as "already loaded" and the module never arrives.
                { src: 'js/modules/app.ai.js', test: () => typeof window.cancelAskMemoraRequest === 'function' },
                // Live lanes, the per-key Live gate and the checker parse. Every Learn module and
                // Ask share one gate per key, so it loads with ai rather than with quiz generation.
                // An IIFE with no top-level names; it reads app.ai.js globals at call time.
                { src: 'js/modules/app.quizgen-routing.js', test: () => window.MemoraQuizGenRouting?.installed === true },
                { src: 'js/modules/app.study-guide-interactive.js', test: () => typeof window.bindMemoraStudyGuideInteractiveFeatures === 'function' },
                // Guided Tutor study visuals: schema validation, layout, and rendering. Its
                // network helper reads app.ai.js globals at call time, so it loads after it.
                { src: 'js/modules/app.study-artifacts.js', test: () => !!window.MemoraStudyArtifacts },
                { src: 'js/modules/app.image-search.js', test: () => !!window.MemoraImageSearch },
                { src: 'js/modules/app.ask-media.js', test: () => !!window.MemoraAskMedia }
            ],
            styles: ['css/study-guide-interactive.css', 'css/study-artifacts.css', 'css/ask-media.css']
        },
        lectureRecorder: {
            label: 'Lecture Recorder',
            deps: ['ai'],
            scripts: [{ src: 'js/modules/app.lecture-recorder.js', test: () => !!window.MemoraLectureRecorder }],
            styles: ['css/lecture-recorder-host.css']
        },
        // The rail footer's Agent: a shell over the Voice Agent in app.ai.js, so ai loads first.
        agent: {
            label: 'Agent controls',
            deps: ['ai'],
            scripts: [
                { src: 'js/modules/app.agent.js', test: () => window.MemoraAgentUI?.installed === true }
            ],
            styles: ['css/agent.css']
        },
        pdfCompressor: {
            label: 'Source compression',
            scripts: [
                { src: 'js/modules/app.pdf-compressor.js', test: () => !!window.MemoraPdfCompressor }
            ]
        },
        quizgen: {
            label: 'Create and generate',
            deps: ['ai', 'pdfCompressor'],
            scripts: [
                { src: 'js/modules/app.quizgen.js', test: () => typeof window.openQuizGenerationPane === 'function' },
                { src: 'js/modules/app.quiz-answer-integrity.js', test: () => window.MemoraQuizAnswerIntegrity?.installed === true }
            ]
        },
        regenerate: {
            label: 'Question regeneration',
            deps: ['ai'],
            scripts: [
                { src: 'js/modules/app.quiz-regenerate.js', test: () => typeof window.undoQuizQuestionRegeneration === 'function' }
            ]
        },
        calendar: {
            label: 'Calendar',
            deps: ['ai'],
            scripts: [
                { src: 'js/modules/app.calendar.js', test: () => typeof window.initCalendarModule === 'function' }
            ],
            styles: ['css/calendar.css']
        },
        clinic: {
            label: 'Memoraful Clinic',
            deps: ['ai'],
            scripts: [
                { src: 'js/modules/app.clinic.js', test: () => typeof window.openClinicModal === 'function' }
            ],
            styles: ['css/clinic.css']
        }
    });

    const FEATURE_LOAD_TIMEOUT_MS = 60000;
    const featurePromises = new Map();
    const featureListeners = new Set();

    function getAssetVersion() {
        const stamp = document.querySelector('meta[name="memora-build"]')?.content;
        return String(stamp || '').trim();
    }

    function withAssetVersion(src) {
        const stamp = getAssetVersion();
        if (!stamp || String(src).includes('?')) return src;
        return `${src}?v=${stamp}`;
    }

    function isFeatureLoaded(name) {
        const feature = featureModules[name];
        if (!feature) return false;
        return feature.scripts.every((entry) => {
            try { return !!entry.test(); } catch (_error) { return false; }
        });
    }

    function notifyFeatureLoaded(name) {
        featureListeners.forEach((listener) => {
            try { listener(name); } catch (error) { console.warn('A feature-loaded listener failed:', error); }
        });
        try {
            document.dispatchEvent(new CustomEvent('memora:feature-loaded', { detail: { feature: name } }));
        } catch (_error) { /* CustomEvent is unavailable in a few embedded webviews. */ }
    }

    function ensureFeature(name) {
        const feature = featureModules[name];
        if (!feature) return Promise.reject(new Error(`Unknown Memoraful feature: ${name}`));
        if (isFeatureLoaded(name)) return Promise.resolve(true);
        if (featurePromises.has(name)) return featurePromises.get(name);

        const loadPromise = (async () => {
            // Dependencies first, then this feature's own scripts in listed order.
            // Both loops are sequential on purpose: these are flat scripts sharing
            // one global scope, so evaluation order is load-bearing.
            for (const dependency of feature.deps || []) {
                await ensureFeature(dependency);
            }
            // Styles land before the scripts run so a surface never paints unstyled.
            await Promise.all((feature.styles || []).map((href) => ensureStyleLoaded({
                href: withAssetVersion(href),
                key: `feature-style:${href}`,
                timeoutMs: FEATURE_LOAD_TIMEOUT_MS
            })));
            for (const entry of feature.scripts) {
                await ensureScriptLoaded({
                    src: withAssetVersion(entry.src),
                    key: `feature-module:${entry.src}`,
                    test: entry.test,
                    timeoutMs: FEATURE_LOAD_TIMEOUT_MS
                });
            }
            notifyFeatureLoaded(name);
            return true;
        })().catch((error) => {
            featurePromises.delete(name);
            throw error;
        });

        featurePromises.set(name, loadPromise);
        return loadPromise;
    }

    function onFeatureLoaded(listener) {
        if (typeof listener !== 'function') return () => { };
        featureListeners.add(listener);
        return () => featureListeners.delete(listener);
    }

    // Run a callback once the document is ready, immediately when it already is.
    // Lazily loaded modules evaluate long after DOMContentLoaded has fired.
    function whenDocumentReady(callback) {
        if (typeof callback !== 'function') return;
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => { callback(); }, { once: true });
            return;
        }
        callback();
    }
    window.MemoraRuntime = Object.freeze({
        maxStudySourceBytes,
        validateStudySourceSize,
        ensureScriptLoaded,
        ensureStyleLoaded,
        ensureFeature,
        isFeatureLoaded,
        onFeatureLoaded,
        whenDocumentReady,
        featureNames: Object.freeze(Object.keys(featureModules)),
        ensurePdfRuntimeLoaded,
        ensurePptxRuntimeLoaded,
        ensureGoogleIdentityLoaded,
        ensureConfettiLoaded,
        ensureKatexLoaded,
        renderMath,
        resourceUrls
    });
})();
